/**
 * Smart context bar — rich, always-visible context display with quick actions.
 *
 * Positioned between the tab bar and page content, it shows:
 *   - Detected context type with icon and colored indicator
 *   - Contextual metadata (cell address, formula preview, dimensions, headers)
 *   - Quick-action buttons relevant to the current context
 *
 * Actions are always visible (not restricted to a single page).
 * The primary recommended action is visually highlighted.
 */

import React from 'react';
import { Button, Text, Tooltip } from '@fluentui/react-components';
import {
  MathFormulaRegular,
  DataBarVerticalRegular,
  TextAlignLeftRegular,
  ChartMultipleRegular,
  BroomRegular,
  TranslateRegular,
  DocumentDismissRegular,
  ChatRegular,
  LightbulbRegular,
  TextBulletListSquareRegular,
} from '@fluentui/react-icons';
import type { ExcelContextState } from '../../office/excel-context';
import type { PageId } from '../../App';

// ─── Props ──────────────────────────────────────────────

interface SmartContextBarProps {
  readonly context: ExcelContextState;
  readonly activePage: PageId;
  readonly onNavigate: (page: PageId) => void;
}

// ─── Action definition ──────────────────────────────────

interface QuickAction {
  readonly page: PageId;
  readonly label: string;
  readonly icon: React.ReactElement;
  /** If true, render as primary (filled) button. */
  readonly primary?: boolean;
}

// ─── Context descriptors ────────────────────────────────

interface ContextDescriptor {
  readonly icon: React.ReactElement;
  readonly variant: 'formula' | 'dataset' | 'text' | 'selection' | 'empty';
  readonly title: string;
  readonly detail: string;
  readonly actions: readonly QuickAction[];
}

/** Truncate a string with ellipsis. */
function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '\u2026' : s;
}

function describeContext(ctx: ExcelContextState): ContextDescriptor {
  // ── Formula ─────────────────────────────────────────
  if (ctx.isFormula) {
    const cellLabel = ctx.activeCellAddress || 'cellule active';
    const formulaPreview = ctx.formulaLocal ?? ctx.formula;
    const detail = formulaPreview
      ? `${cellLabel}  \u2022  ${truncate(formulaPreview, 42)}`
      : cellLabel;
    return {
      icon: <MathFormulaRegular />,
      variant: 'formula',
      title: 'Formule detectee',
      detail,
      actions: [
        { page: 'explain', label: 'Expliquer', icon: <LightbulbRegular />, primary: true },
        { page: 'formulas', label: 'Generer', icon: <MathFormulaRegular /> },
      ],
    };
  }

  // ── Dataset with chart potential ────────────────────
  if (ctx.isDataset && ctx.canChart) {
    const dims = `${ctx.rowCount} lignes \u00d7 ${ctx.columnCount} colonnes`;
    const headers = ctx.probableHeaders.length > 0
      ? `\n${truncate(ctx.probableHeaders.join(', '), 60)}`
      : '';
    return {
      icon: <DataBarVerticalRegular />,
      variant: 'dataset',
      title: 'Donnees detectees',
      detail: dims + headers,
      actions: [
        { page: 'analysis', label: 'Analyser', icon: <DataBarVerticalRegular />, primary: true },
        { page: 'chart', label: 'Graphique', icon: <ChartMultipleRegular /> },
        { page: 'summary', label: 'Resumer', icon: <TextBulletListSquareRegular /> },
        { page: 'cleaning', label: 'Nettoyer', icon: <BroomRegular /> },
      ],
    };
  }

  // ── Dataset without chart potential ─────────────────
  if (ctx.isDataset) {
    const dims = `${ctx.rowCount} lignes \u00d7 ${ctx.columnCount} colonnes`;
    const headers = ctx.probableHeaders.length > 0
      ? `\n${truncate(ctx.probableHeaders.join(', '), 60)}`
      : '';
    return {
      icon: <DataBarVerticalRegular />,
      variant: 'dataset',
      title: 'Donnees detectees',
      detail: dims + headers,
      actions: [
        { page: 'analysis', label: 'Analyser', icon: <DataBarVerticalRegular />, primary: true },
        { page: 'summary', label: 'Resumer', icon: <TextBulletListSquareRegular /> },
        { page: 'cleaning', label: 'Nettoyer', icon: <BroomRegular /> },
      ],
    };
  }

  // ── Textual selection ──────────────────────────────
  if (ctx.isTextual) {
    const count = ctx.nonEmptyCellCount;
    return {
      icon: <TextAlignLeftRegular />,
      variant: 'text',
      title: 'Selection textuelle',
      detail: `${count} cellule${count > 1 ? 's' : ''} non vide${count > 1 ? 's' : ''}`,
      actions: [
        { page: 'translate', label: 'Traduire', icon: <TranslateRegular />, primary: true },
        { page: 'cleaning', label: 'Nettoyer', icon: <BroomRegular /> },
        { page: 'summary', label: 'Resumer', icon: <TextBulletListSquareRegular /> },
      ],
    };
  }

  // ── Non-empty selection (mixed / numeric) ──────────
  if (ctx.hasSelection) {
    return {
      icon: <DataBarVerticalRegular />,
      variant: 'selection',
      title: 'Selection active',
      detail: `${ctx.nonEmptyCellCount} cellule${ctx.nonEmptyCellCount > 1 ? 's' : ''} non vide${ctx.nonEmptyCellCount > 1 ? 's' : ''}`,
      actions: [
        { page: 'cleaning', label: 'Nettoyer', icon: <BroomRegular />, primary: true },
        { page: 'formulas', label: 'Generer', icon: <MathFormulaRegular /> },
      ],
    };
  }

  // ── Empty / no data ────────────────────────────────
  return {
    icon: <DocumentDismissRegular />,
    variant: 'empty',
    title: 'Aucune donnee exploitable',
    detail: ctx.sheetHasUsedRange ? 'Selectionnez une plage avec des donnees' : 'Feuille vide',
    actions: [
      { page: 'assistant', label: 'Assistant', icon: <ChatRegular />, primary: true },
      { page: 'formulas', label: 'Generer', icon: <MathFormulaRegular /> },
    ],
  };
}

// ─── Component ──────────────────────────────────────────

export const SmartContextBar: React.FC<SmartContextBarProps> = ({
  context,
  activePage,
  onNavigate,
}) => {
  const desc = describeContext(context);

  // Split detail into lines (headers go on a second line)
  const detailLines = desc.detail.split('\n');

  return (
    <div className={`smart-ctx smart-ctx--${desc.variant}`}>
      {/* Left: icon + text */}
      <div className="smart-ctx-info">
        <span className="smart-ctx-icon">{desc.icon}</span>
        <div className="smart-ctx-text">
          <Text size={200} weight="semibold" className="smart-ctx-title">
            {desc.title}
          </Text>
          <Tooltip content={desc.detail.replace('\n', ' — ')} relationship="description">
            <Text size={100} className="smart-ctx-detail">
              {detailLines[0]}
            </Text>
          </Tooltip>
          {detailLines[1] && (
            <Tooltip content={detailLines[1]} relationship="description">
              <Text size={100} className="smart-ctx-headers">
                {detailLines[1]}
              </Text>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Right: actions */}
      {desc.actions.length > 0 && (
        <div className="smart-ctx-actions">
          {desc.actions.map((action) => (
            <Button
              key={action.page}
              size="small"
              appearance={action.primary ? 'primary' : 'subtle'}
              icon={action.icon}
              onClick={() => onNavigate(action.page)}
              disabled={activePage === action.page}
              className="smart-ctx-btn"
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
